import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navigationItems, NavItem } from '@/lib/navigation';
import logoRed from '@/assets/logo-red.png';
import logoWhite from '@/assets/logo-white.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isDarkBehind, setIsDarkBehind] = useState(true);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();

  const logoRef = useRef<HTMLDivElement>(null);

  const checkBackground = useCallback(() => {
    // Sample at the logo's exact position, not the full header width
    const logoEl = logoRef.current;
    if (!logoEl) return;
    const rect = logoEl.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    const elements = document.elementsFromPoint(x, y);
    
    for (const el of elements) {
      // Skip the header itself and its children
      if (headerRef.current?.contains(el)) continue;
      
      const style = getComputedStyle(el);
      const bgImage = style.backgroundImage;

      if (bgImage && bgImage !== 'none' && !bgImage.includes('linear-gradient(hsl(')) {
        setIsDarkBehind(true);
        return;
      }

      const bg = style.backgroundColor;
      const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = bg.includes('rgba') ? parseFloat(bg.split(',')[3]) : 1;
        if (a > 0.1) {
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          setIsDarkBehind(luminance < 0.5);
          return;
        }
      }
    }
    setIsDarkBehind(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      checkBackground();
    };
    window.addEventListener('scroll', handleScroll);

    // Robust initial check: retry until background is detected after images load
    const runInitialCheck = () => {
      requestAnimationFrame(() => {
        checkBackground();
      });
    };
    runInitialCheck();
    const t1 = setTimeout(runInitialCheck, 200);
    const t2 = setTimeout(runInitialCheck, 500);
    const t3 = setTimeout(runInitialCheck, 1000);
    window.addEventListener('load', runInitialCheck);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', runInitialCheck);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [checkBackground]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    // Re-check after route change with slight delay for render
    const timer = setTimeout(checkBackground, 100);
    return () => clearTimeout(timer);
  }, [location, checkBackground]);

  const renderNavItem = (item: NavItem) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div
          key={item.label}
          className="nav-item relative"
          onMouseEnter={() => setOpenDropdown(item.label)}
          onMouseLeave={() => setOpenDropdown(null)}
        >
          <button className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors duration-300 text-[13px] font-normal tracking-wide py-2">
            {item.label}
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
          </button>
          
          <div className={`dropdown-menu ${openDropdown === item.label ? 'opacity-100 visible' : ''}`}>
            {item.children?.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                className="dropdown-item"
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return (
      <Link
        key={item.label}
        to={item.href}
        className="nav-link text-white/80 hover:text-white transition-colors duration-300 text-[13px] font-normal tracking-wide py-2"
      >
        {item.label}
      </Link>
    );
  };

  return (
    <motion.header
      ref={headerRef}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'py-3' : 'py-5'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className={`glass-panel px-6 md:px-8 py-4 flex items-center justify-between transition-all duration-500 ${
          isScrolled ? 'shadow-lg' : ''
        }`}>
          {/* Logo & Navigation - Left aligned */}
          <div className="flex items-center gap-8 md:gap-10">
            <Link to="/" className="flex items-center flex-shrink-0">
              <div ref={logoRef} className="relative h-12 md:h-14 w-12 md:w-14">
                <img 
                  src={logoWhite} 
                  alt="Pharmakademie" 
                  className="h-full w-full object-contain"
                />
              </div>
            </Link>

            {/* Desktop & Tablet Navigation */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-10">
              {navigationItems.map(renderNavItem)}
            </nav>
          </div>

          {/* CTA Button - Right aligned */}
          <div className="hidden md:flex items-center">
            <Link to="/beratung/individuell">
              <button 
                className="logo-zoom-btn group font-medium text-sm tracking-wide px-8 py-3.5 rounded-lg text-foreground transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_hsl(357_99%_45%/0.5)]"
              >
                <div className="btn-logo" />
                <span className="btn-text">Beratung buchen</span>
              </button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="glass-panel mt-2 p-6 md:hidden max-h-[80vh] overflow-y-auto"
            >
              <nav className="flex flex-col gap-2">
                {navigationItems.map((item) => (
                  <div key={item.label}>
                    {item.children ? (
                      <div>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                          className="w-full flex items-center justify-between text-white font-medium py-3 border-b border-white/20"
                        >
                          {item.label}
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {openDropdown === item.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="py-2 pl-4">
                                {item.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    className="block py-2.5 text-white/70 hover:text-white transition-colors text-sm"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className="block text-white font-medium py-3 border-b border-white/20"
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                <Link to="/beratung/individuell" className="mt-4">
                  <Button variant="outline" size="lg" className="w-full font-medium text-white border-white/30 hover:bg-white hover:text-foreground">
                    Beratung buchen
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
